// as-cetra-vet-ui/src/components/authenticated/handout/customise-mode/SaveCustomisedHandoutModal.js

import React, { useState, useEffect, useContext } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Radio,
    RadioGroup,
    FormControlLabel,
    Box,
    Autocomplete,
    FormControl,
    Checkbox,
    Typography,
} from '@mui/material';
import StoreContext from '../../../../react-core-lib/components/app/store-context';
import UserApi from '../../../../rest-api/user-api';
import { useSnackbar } from '../../SnackbarProvider';
import BlockingSpinner from '../../../UIControls/BlockingSpinner';
import { useCustomiseMode } from './CustomiseModeContext';
// Phase 2: Import WB save dependencies
import { mergeCanvasWithIllustration } from '../whiteboard/mergeCanvasWithIllustration';
import { handleCreateOrUpdateWhiteboard } from '../whiteboard/WhiteboardService';
import { getDocumentTitle } from '../../../../components/helper/contentOperation';
import DownloadUtils from '../../../../utils/download-utils';

const SaveCustomisedHandoutModal = ({
    open,
    onClose,
    handoutData,
    editedContentData,
    onSaved,
    // Phase 2: Combined mode props
    isCombinedMode = false,
    hasActiveWhiteboardDrawings = false,
    activeCanvasRef = null,
    canvasRef = null,
}) => {
    const { userDataStore, authStore, customisedHandoutStore, whiteboardStore, imageStore } = useContext(StoreContext);
    const { selectedWhiteboard } = useCustomiseMode();
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveToMyLibrary, setSaveToMyLibrary] = useState(true); // default to My Library
    const showSnackbar = useSnackbar();

    // Patient-related state (for My Clinic)
    const [patientList, setPatientList] = useState([]);
    const [patientType, setPatientType] = useState('existing');
    const [selectedPatient, setSelectedPatient] = useState(''); // For existing patient ID
    const [manualPatientName, setManualPatientName] = useState(''); // For new patient entry
    const [clientSurname, setClientSurname] = useState('');
    const [clinicPatientId, setClinicPatientId] = useState('');
    const [includeAdditionalInfo, setIncludeAdditionalInfo] = useState(false);
    const [dob, setDob] = useState('');
    const [breed, setBreed] = useState('');
    const [species, setSpecies] = useState('');
    const [microchip, setMicrochip] = useState('');

    const isStudent = userDataStore?.subscriptionData?.accountType === 'Student';
    const accountType = userDataStore?.subscriptionData?.accountType;

    useEffect(() => {
        if (open) {
            setNotes('');
        }
    }, [open]);

    useEffect(() => {
        if (['Student', 'Professional'].includes(accountType)) {
            setSaveToMyLibrary(true);
        }
    }, [accountType]);

    useEffect(() => {
        if (!isStudent && open && !saveToMyLibrary) {
            fetchPatientList();
        }
    }, [isStudent, open, saveToMyLibrary]);

    const fetchPatientList = async () => {
        try {
            const response = await UserApi.getPatientsByFilter(authStore.idToken, {
                filterType: 'patientStatus',
                filterValue: 'active',
            });

            if (response?.patients) {
                setPatientList(response.patients);
            }
        } catch (error) {
            console.error('Failed to fetch patients:', error);
        }
    };

    const formatPatientDisplay = (patient) => {
        return `${patient.patientName} | ${patient.clientSurname || 'N/A'} (${patient.species || 'N/A'} | ${patient.breed || 'N/A'} | ${patient.microchipNumber || 'N/A'} | ${patient.DOB || 'N/A'} | ${patient.clinicPatientId || 'N/A'})`;
    };

    const fetchClinicianData = async () => {
        try {
            console.warn('Fetching Clinician data...');
            const clinicianDataResponse = await UserApi.getClinicianData(authStore.idToken);
            return clinicianDataResponse;
        } catch (error) {
            console.error('Failed to fetch clinician data:', error);
            return null;
        }
    };

    const handleSave = async () => {
        if (saving) return;
        setSaving(true);

        // Validate patient selection for My Clinic
        if (!isStudent && !saveToMyLibrary) {
            if (patientType === 'existing' && !selectedPatient) {
                showSnackbar('Please select an existing patient.', 'error');
                setSaving(false);
                return;
            }
            if (patientType === 'new') {
                if (!manualPatientName.trim()) {
                    showSnackbar('Patient name is required.', 'error');
                    setSaving(false);
                    return;
                }

                if (!clientSurname.trim()) {
                    showSnackbar('Client surname is required.', 'error');
                    setSaving(false);
                    return;
                }
            }
        }

        try {
            console.warn('Fetching clinician data before saving...');
            const clinicianData = await fetchClinicianData();

            if (!clinicianData.userId) {
                console.error('Error: clinicianData.userId is undefined.');
                showSnackbar('Failed to retrieve user data. Please try again.', 'error');
                setSaving(false);
                return;
            }

            // Determine patient names
            let finalPatientName = null;
            let finalClientSurname = null;
            let finalPatientId = null;

            if (!saveToMyLibrary) {
                if (patientType === 'existing') {
                    const existing = patientList.find((p) => p.patientId === selectedPatient);
                    finalPatientName = existing ? existing.patientName : '';
                    finalClientSurname = existing ? existing.clientSurname : '';
                    finalPatientId = selectedPatient;
                } else {
                    // New patient - create patient record first
                    finalPatientName = manualPatientName.trim();
                    finalClientSurname = clientSurname.trim();

                    const newPatientData = {
                        patientName: finalPatientName,
                        clientSurname: finalClientSurname,
                    };

                    if (clinicPatientId) newPatientData.clinicPatientId = clinicPatientId;
                    if (dob) newPatientData.DOB = dob;
                    if (breed) newPatientData.breed = breed;
                    if (species) newPatientData.species = species;
                    if (microchip) newPatientData.microchipNumber = microchip;

                    try {
                        console.warn('Creating a new patient with data:', newPatientData);
                        const patientResp = await UserApi.createOrUpdatePatient(authStore.idToken, newPatientData);

                        if (!patientResp?.patientId) {
                            console.error('Failed to create patient: Unexpected response', patientResp);
                            showSnackbar('Failed to create patient. The response did not contain a valid patientId.', 'error');
                            setSaving(false);
                            return;
                        }

                        finalPatientId = patientResp.patientId;
                        console.warn(`New patient created with ID: ${finalPatientId}`);
                    } catch (error) {
                        console.error('Error creating patient:', error);
                        showSnackbar('Failed to create patient. Please try again.', 'error');
                        setSaving(false);
                        return;
                    }
                }
            }

            // Replace illustration with whiteboard if selected
            let finalContentData = JSON.parse(JSON.stringify(editedContentData)); // Deep clone
            if (selectedWhiteboard) {
                console.log('[Save CH] Replacing illustration with whiteboard:', selectedWhiteboard.canvasImageLink);
                // Find and replace the main illustration src
                const locale = 'en-UK';
                if (finalContentData[locale]) {
                    for (const section of finalContentData[locale]) {
                        if (section.blocks) {
                            for (const block of section.blocks) {
                                if (block.type === 'illustration' && section.sectionName === 'introduction') {
                                    // Replace with whiteboard image path
                                    block.src = selectedWhiteboard.canvasImageLink;
                                    console.log('[Save CH] Replaced illustration src:', block.src);
                                    break;
                                }
                            }
                        }
                    }
                }
            }

            // Prepare payload for save
            const payload = {
                customisedId: null, // Will be generated by backend
                baseContentId: handoutData.contentId,
                baseLastUpdated: handoutData.lastUpdated,
                locale: 'en-UK',
                effectiveContentData: finalContentData,
                attachedWhiteboardId: selectedWhiteboard ? selectedWhiteboard.whiteboardId : null,
                attachedWhiteboardCanvasImageLink: selectedWhiteboard ? selectedWhiteboard.canvasImageLink : null,
                attachedWhiteboardETag: selectedWhiteboard ? selectedWhiteboard.eTag : null,
                notes: notes.trim() || null,
                customisedStatus: 'active',
                updatedById: userDataStore.userId,
                updatedByFirstName: userDataStore.userData.firstName || null,
                updatedByLastName: userDataStore.userData.lastName || null,
                baseContentIdUserIdCompositeKey: `${handoutData.contentId}#${clinicianData.userId}`,
                // Patient fields (for My Clinic)
                patientId: saveToMyLibrary ? null : finalPatientId,
                patientName: saveToMyLibrary ? null : finalPatientName,
                clientSurname: saveToMyLibrary ? null : finalClientSurname,
                microchipNumber: saveToMyLibrary ? null : microchip || null,
                clinicPatientId: saveToMyLibrary ? null : clinicPatientId || null,
            };

            console.warn('Saving customised handout...');
            const response = await UserApi.createOrUpdateCustomisedHandout(authStore.idToken, payload);
            console.warn('Saved customised handout result:', response);

            if (response && response.customisedId) {
                // Backend returns { message, customisedId, customisedStatus }
                // We need to construct the full object for the store

                // Construct full customised handout object for store
                const fullCustomisedHandout = {
                    customisedId: response.customisedId,
                    userId: clinicianData.userId,
                    baseContentId: payload.baseContentId,
                    baseLastUpdated: payload.baseLastUpdated,
                    locale: payload.locale,
                    effectiveContentData: payload.effectiveContentData,
                    attachedWhiteboardId: payload.attachedWhiteboardId,
                    notes: payload.notes,
                    customisedStatus: response.customisedStatus,
                    lastUpdated: Date.now(), // Use timestamp number like backend
                    updatedBy: userDataStore.userData.emailAddress,
                    updatedById: userDataStore.userId,
                    updatedByFirstName: userDataStore.userData.firstName || null,
                    updatedByLastName: userDataStore.userData.lastName || null,
                    baseContentIdUserIdCompositeKey: `${payload.baseContentId}#${clinicianData.userId}`,
                    // Patient fields (for My Clinic)
                    patientId: payload.patientId,
                    patientName: payload.patientName,
                    clientSurname: payload.clientSurname,
                    microchipNumber: payload.microchipNumber,
                    clinicPatientId: payload.clinicPatientId,
                };

                localStorage.setItem(
                    'lastCustomisedHandoutState',
                    JSON.stringify({
                        customisedId: response.customisedId,
                        baseContentId: payload.baseContentId,
                        userId: clinicianData.userId,
                        updatedByFirstName: payload.updatedByFirstName || null,
                        updatedByLastName: payload.updatedByLastName || null,
                        patientName: payload.patientName || null,
                        clientSurname: payload.clientSurname || null,
                        patientId: payload.patientId || null,
                        microchipNumber: payload.microchipNumber || null,
                        clinicPatientId: payload.clinicPatientId || null,
                    }),
                );

                // Add/update in CustomisedHandoutStore
                if (customisedHandoutStore) {
                    await customisedHandoutStore.addOrUpdateCustomisedHandout(fullCustomisedHandout);
                    console.log('Customised handout added to store:', response.customisedId);
                }

                // Update userDataStore for My Library
                // Note: Students should never see this feature (Professional/Clinic only)
                if (saveToMyLibrary) {
                    const currentCustomisedHandouts = userDataStore.userData.customisedHandouts || [];
                    console.log('Current customisedHandouts in userDataStore:', currentCustomisedHandouts);
                    console.log('Saving to My Library, saveToMyLibrary:', saveToMyLibrary);

                    // Optional: enforce the 300 limit (same as whiteboards)
                    if (currentCustomisedHandouts.length >= 300) {
                        showSnackbar(
                            'You have reached the maximum number of customised handouts (300) in My Library.',
                            'warning',
                        );
                        setSaving(false);
                        return;
                    }

                    // Check if already exists in array
                    if (!currentCustomisedHandouts.includes(response.customisedId)) {
                        const updatedCustomisedHandouts = [...currentCustomisedHandouts, response.customisedId];
                        console.log('Updated customisedHandouts array:', updatedCustomisedHandouts);

                        // Update userDataStore locally
                        userDataStore.setUserData({
                            ...userDataStore.userData,
                            customisedHandouts: updatedCustomisedHandouts,
                        });

                        // Sync with backend
                        const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');
                        console.log('Is multiuser account:', isMultiUser);
                        console.log('Account type:', userDataStore?.subscriptionData?.accountType);
                        console.log('User data:', {
                            emailAddress: userDataStore.userData.emailAddress,
                            inviteId: userDataStore.userData.inviteId,
                        });

                        const updatePayload = {
                            customisedHandouts: updatedCustomisedHandouts,
                        };

                        try {
                            if (isMultiUser) {
                                updatePayload.action = 'updateMultiuser';
                                updatePayload.emailAddress = userDataStore.userData.emailAddress;
                                updatePayload.inviteId = userDataStore.userData.inviteId;
                                console.log('Calling updateClinicianDataMultiuser with payload:', updatePayload);
                                const result = await UserApi.updateClinicianDataMultiuser(
                                    authStore.idToken,
                                    updatePayload,
                                );
                                console.log('updateClinicianDataMultiuser result:', result);
                            } else {
                                console.log('Calling updateClinicianData with payload:', updatePayload);
                                const result = await UserApi.updateClinicianData(authStore.idToken, updatePayload);
                                console.log('updateClinicianData result:', result);
                            }
                            console.log('Successfully synced customisedHandouts with backend');
                        } catch (err) {
                            console.error('Failed to sync customisedHandouts with backend', err);
                            showSnackbar('Failed to sync with backend. Please refresh the page.', 'warning');
                        }
                    } else {
                        console.log('Customised handout already exists in array, skipping backend update');
                    }
                }

                // Phase 2: Combined mode - save whiteboard if active
                let whiteboardResponse = null;
                if (isCombinedMode && hasActiveWhiteboardDrawings && canvasRef?.current) {
                    console.warn('[Combined Mode] Saving whiteboard with customised handout...');
                    try {
                        // Merge canvas with illustration
                        const mergedBlob = await mergeCanvasWithIllustration(canvasRef);

                        // Get canvas JSON
                        const rawJson = canvasRef.current.toJSON();
                        rawJson.canvasWidth = canvasRef.current.getWidth();
                        rawJson.canvasHeight = canvasRef.current.getHeight();
                        const canvasJson = JSON.stringify(rawJson);

                        // Generate filename
                        const date = new Date();
                        const formattedDateTime = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}_${String(date.getHours()).padStart(2, '0')}${String(date.getMinutes()).padStart(2, '0')}${String(date.getSeconds()).padStart(2, '0')}`;
                        const handoutShortName = handoutData.contentName.split(' ').slice(0, 3).join('');
                        const fileName = `${formattedDateTime}_${handoutShortName}`;

                        // Check if diagram handout
                        const locale = 'en-UK';
                        const isDiagramHandout = handoutData?.contentData?.[locale]?.some(
                            (section) => section.sectionName === 'diagram',
                        );
                        const diagramVariant = isDiagramHandout
                            ? handoutData.showLabels
                                ? 'withLabels'
                                : 'noLabels'
                            : null;

                        // Prepare WB save data
                        const wbSaveData = {
                            userId: clinicianData.userId,
                            patientId: saveToMyLibrary ? null : finalPatientId,
                            patientName: finalPatientName,
                            clientSurname: finalClientSurname,
                            clinicPatientId,
                            dob,
                            breed,
                            species,
                            microchip,
                            notes,
                            fileName,
                            canvasData: canvasJson,
                            mergedImageBlob: mergedBlob,
                            handoutId: handoutData.contentId,
                            diagramVariant,
                            handoutContentName: handoutData.contentName,
                            handoutAlphabeticalName: handoutData.alphabeticalName,
                            handoutDocumentTitle: getDocumentTitle(handoutData.contentData, locale),
                            whiteboardStatus: 'active',
                            myLibrary: saveToMyLibrary,
                            updatedById: userDataStore.userId,
                            updatedByFirstName: userDataStore.userData.firstName || null,
                            updatedByLastName: userDataStore.userData.lastName || null,
                            lastUsedColours: null,
                        };

                        // Save whiteboard
                        whiteboardResponse = await handleCreateOrUpdateWhiteboard(
                            authStore.idToken,
                            wbSaveData,
                            showSnackbar,
                        );
                        console.warn('[Combined Mode] Whiteboard saved:', whiteboardResponse);

                        // Update whiteboard store
                        if (whiteboardResponse) {
                            const newWhiteboard = {
                                whiteboardId: whiteboardResponse.whiteboardId,
                                canvasImageLink: whiteboardResponse.canvasImageLink,
                                canvasJsonLink: whiteboardResponse.canvasJsonLink,
                                eTag: whiteboardResponse.eTag || null,
                                fileName,
                                handoutId: handoutData.contentId,
                                diagramVariant,
                                handoutContentName: handoutData.contentName,
                                handoutAlphabeticalName: handoutData.alphabeticalName,
                                handoutDocumentTitle: getDocumentTitle(handoutData.contentData, locale),
                                notes,
                                whiteboardStatus: 'active',
                                userId: clinicianData.userId,
                                lastUsedColours: null,
                                patientId: saveToMyLibrary ? null : finalPatientId,
                                patientName: saveToMyLibrary ? null : finalPatientName,
                                clientSurname: saveToMyLibrary ? null : finalClientSurname,
                                clinicPatientId: saveToMyLibrary ? null : clinicPatientId || null,
                                microchipNumber: saveToMyLibrary ? null : microchip || null,
                                handoutIdUserIdCompositeKey: `${handoutData.contentId}#${clinicianData.userId}`,
                                lastUpdated: new Date().toISOString(),
                                updatedById: userDataStore.userId,
                                updatedByFirstName: userDataStore.userData.firstName || null,
                                updatedByLastName: userDataStore.userData.lastName || null,
                            };

                            await whiteboardStore.setWhiteboardData([newWhiteboard]);

                            localStorage.setItem(
                                'lastWhiteboardState',
                                JSON.stringify({
                                    whiteboardId: whiteboardResponse.whiteboardId,
                                    handoutId: handoutData.contentId,
                                    canvasImageLink: whiteboardResponse.canvasImageLink,
                                    canvasJsonLink: whiteboardResponse.canvasJsonLink,
                                    fileName,
                                    eTag: whiteboardResponse.eTag || null,
                                    diagramVariant,
                                    updatedByFirstName: userDataStore.userData.firstName || null,
                                    updatedByLastName: userDataStore.userData.lastName || null,
                                    patientId: saveToMyLibrary ? null : finalPatientId,
                                    patientName: saveToMyLibrary ? null : finalPatientName,
                                    clientSurname: saveToMyLibrary ? null : finalClientSurname,
                                    clinicPatientId: saveToMyLibrary ? null : clinicPatientId || null,
                                    microchipNumber: saveToMyLibrary ? null : microchip || null,
                                }),
                            );

                            // Update userDataStore for My Library
                            if (saveToMyLibrary && whiteboardResponse.whiteboardId) {
                                const currentWhiteboards = userDataStore.userData.whiteboards || [];
                                if (currentWhiteboards.length < 300) {
                                    const updatedWhiteboards = [...currentWhiteboards, whiteboardResponse.whiteboardId];
                                    userDataStore.setUserData({
                                        ...userDataStore.userData,
                                        whiteboards: updatedWhiteboards,
                                    });

                                    // Sync with backend
                                    const isMultiUser =
                                        userDataStore?.subscriptionData?.accountType?.includes('multiuser');
                                    const wbPayload = { whiteboards: updatedWhiteboards };

                                    try {
                                        if (isMultiUser) {
                                            wbPayload.action = 'updateMultiuser';
                                            wbPayload.emailAddress = userDataStore.userData.emailAddress;
                                            wbPayload.inviteId = userDataStore.userData.inviteId;
                                            await UserApi.updateClinicianDataMultiuser(authStore.idToken, wbPayload);
                                        } else {
                                            await UserApi.updateClinicianData(authStore.idToken, wbPayload);
                                        }
                                    } catch (err) {
                                        console.error('[Combined Mode] Failed to sync whiteboards with backend', err);
                                    }
                                }
                            }

                            // Save image to IndexedDB
                            if (whiteboardResponse.canvasImageLink) {
                                const imageExists = await imageStore.getImageFromIndexedDB(
                                    whiteboardResponse.canvasImageLink,
                                );
                                if (!imageExists) {
                                    const payload = {
                                        files: [{ method: 'get', key: whiteboardResponse.canvasImageLink }],
                                        contentCategory: 'whiteboard',
                                    };
                                    const resp = await UserApi.generatePresignedUrls(authStore.idToken, payload);
                                    const presignedUrl = resp.getUrls[0].presignedUrl;
                                    const downloadResult =
                                        await DownloadUtils.downloadImageFromPresignedUrl(presignedUrl);
                                    await imageStore.saveImageToIndexedDB(
                                        whiteboardResponse.canvasImageLink,
                                        downloadResult.base64data,
                                        whiteboardResponse.eTag || null,
                                    );
                                }
                            }

                            // Update CH record with attached whiteboard
                            const updatedCHPayload = {
                                ...payload,
                                customisedId: response.customisedId,
                                attachedWhiteboardId: whiteboardResponse.whiteboardId,
                                attachedWhiteboardCanvasImageLink: whiteboardResponse.canvasImageLink,
                                attachedWhiteboardETag: whiteboardResponse.eTag || null,
                            };

                            // Update effectiveContentData to replace illustration with new WB
                            const updatedContentData = JSON.parse(JSON.stringify(finalContentData));
                            if (updatedContentData[locale]) {
                                for (const section of updatedContentData[locale]) {
                                    if (section.blocks) {
                                        for (const block of section.blocks) {
                                            if (
                                                block.type === 'illustration' &&
                                                section.sectionName === 'introduction'
                                            ) {
                                                block.src = whiteboardResponse.canvasImageLink;
                                                console.log(
                                                    '[Combined Mode] Updated CH illustration with new WB:',
                                                    block.src,
                                                );
                                                break;
                                            }
                                        }
                                    }
                                }
                            }
                            updatedCHPayload.effectiveContentData = updatedContentData;

                            console.warn('[Combined Mode] Updating CH with attached whiteboard...');
                            await UserApi.createOrUpdateCustomisedHandout(authStore.idToken, updatedCHPayload);

                            // Update local store
                            fullCustomisedHandout.attachedWhiteboardId = whiteboardResponse.whiteboardId;
                            fullCustomisedHandout.attachedWhiteboardCanvasImageLink =
                                whiteboardResponse.canvasImageLink;
                            fullCustomisedHandout.attachedWhiteboardETag = whiteboardResponse.eTag || null;
                            fullCustomisedHandout.effectiveContentData = updatedContentData;
                            if (customisedHandoutStore) {
                                await customisedHandoutStore.addOrUpdateCustomisedHandout(fullCustomisedHandout);
                            }
                        }
                    } catch (error) {
                        console.error('[Combined Mode] Failed to save whiteboard:', error);
                        showSnackbar(
                            'Customised handout saved, but whiteboard save failed. Please try again.',
                            'warning',
                        );
                    }
                }

                showSnackbar(
                    isCombinedMode && whiteboardResponse
                        ? 'Customised handout and whiteboard successfully saved!'
                        : 'Customised handout successfully saved!',
                    'success',
                );

                if (onSaved) onSaved();
                else onClose();
            } else {
                showSnackbar('Failed to save customised handout. Please try again.', 'error');
            }
        } catch (error) {
            console.error('Error saving customised handout:', error);
            showSnackbar('Failed to save customised handout. Please try again.', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {isCombinedMode && hasActiveWhiteboardDrawings
                    ? 'Save Customised Handout & Whiteboard'
                    : 'Save Customised Handout'}
            </DialogTitle>
            <DialogContent>
                {saving && <BlockingSpinner open={true} />}
                {/* Phase 3: Info message for combined mode */}
                {isCombinedMode && hasActiveWhiteboardDrawings && (
                    <Box
                        sx={{
                            mt: 2,
                            p: 2,
                            bgcolor: '#E3F2FD',
                            borderRadius: 1,
                            border: '1px solid #90CAF9',
                        }}
                    >
                        <Typography variant="body2" sx={{ color: '#1565C0', fontWeight: 500 }}>
                            Combined save mode
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#424242', mt: 0.5 }}>
                            Your customised handout and whiteboard drawings will be saved together to the same location.
                        </Typography>
                    </Box>
                )}
                {!isStudent && (
                    <>
                        <Box sx={{ mt: 2 }}>
                            <RadioGroup
                                row
                                value={saveToMyLibrary ? 'library' : 'clinic'}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setSaveToMyLibrary(val === 'library');
                                }}
                            >
                                <FormControlLabel value="library" control={<Radio />} label="Save to My Library" />
                                <FormControlLabel value="clinic" control={<Radio />} label="Save to My Clinic" />
                            </RadioGroup>
                            <Box sx={{ fontSize: '0.875rem', color: 'rgba(32, 31, 32, 0.4)', mt: 0.5 }}>
                                *Please select one option: My Library or My Clinic
                            </Box>
                        </Box>

                        {!saveToMyLibrary && (
                            <>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{ mt: 2, mb: 2 }}
                                >
                                    <RadioGroup
                                        row
                                        value={patientType}
                                        onChange={(e) => {
                                            setPatientType(e.target.value);
                                            if (e.target.value === 'new') {
                                                setSelectedPatient('');
                                                setClinicPatientId('');
                                                setDob('');
                                                setBreed('');
                                                setSpecies('');
                                                setMicrochip('');
                                            } else {
                                                setManualPatientName('');
                                                setClientSurname('');
                                            }
                                        }}
                                    >
                                        <FormControlLabel
                                            value="existing"
                                            control={<Radio />}
                                            label="Existing Patient"
                                        />
                                        <FormControlLabel value="new" control={<Radio />} label="New Patient" />
                                    </RadioGroup>
                                </Box>

                                {patientType === 'existing' && (
                                    <FormControl fullWidth margin="normal">
                                        <Autocomplete
                                            fullWidth
                                            options={patientList}
                                            getOptionLabel={(option) =>
                                                formatPatientDisplay(
                                                    typeof option === 'string' ? { patientName: option } : option,
                                                )
                                            }
                                            value={patientList.find((p) => p.patientId === selectedPatient) || null}
                                            onChange={(e, newValue) => {
                                                if (newValue) {
                                                    setSelectedPatient(newValue.patientId);
                                                    setClinicPatientId(newValue.clinicPatientId || '');
                                                    setDob(newValue.DOB || '');
                                                    setBreed(newValue.breed || '');
                                                    setSpecies(newValue.species || '');
                                                    setMicrochip(newValue.microchipNumber || '');
                                                } else {
                                                    setSelectedPatient('');
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Select Patient" margin="normal" />
                                            )}
                                            renderOption={(props, option) => (
                                                <Box
                                                    component="li"
                                                    {...props}
                                                    sx={{
                                                        fontSize: '0.8rem',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                >
                                                    {formatPatientDisplay(option)}
                                                </Box>
                                            )}
                                            filterOptions={(options, { inputValue }) =>
                                                options.filter((patient) => {
                                                    const query = inputValue.toLowerCase();
                                                    return (
                                                        patient.patientName?.toLowerCase().includes(query) ||
                                                        patient.clinicPatientId?.toLowerCase().includes(query) ||
                                                        patient.microchipNumber?.toLowerCase().includes(query) ||
                                                        patient.breed?.toLowerCase().includes(query) ||
                                                        patient.species?.toLowerCase().includes(query) ||
                                                        patient.DOB?.toLowerCase().includes(query)
                                                    );
                                                })
                                            }
                                        />
                                    </FormControl>
                                )}

                                {patientType === 'new' && (
                                    <>
                                        <TextField
                                            label="Enter New Patient Name"
                                            fullWidth
                                            margin="normal"
                                            value={manualPatientName}
                                            onChange={(e) => setManualPatientName(e.target.value)}
                                            required
                                        />
                                        <TextField
                                            label="Client Surname"
                                            fullWidth
                                            margin="normal"
                                            value={clientSurname}
                                            onChange={(e) => setClientSurname(e.target.value)}
                                            required
                                        />
                                    </>
                                )}

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={includeAdditionalInfo}
                                            onChange={(e) => setIncludeAdditionalInfo(e.target.checked)}
                                        />
                                    }
                                    label={
                                        patientType === 'existing'
                                            ? 'Show Patient Details'
                                            : 'Add Additional Patient Details'
                                    }
                                />
                                {includeAdditionalInfo && (
                                    <>
                                        <TextField
                                            label="Client Surname"
                                            fullWidth
                                            margin="normal"
                                            value={clientSurname || 'N/A'}
                                            onChange={(e) => setClientSurname(e.target.value)}
                                            disabled={patientType === 'existing'}
                                        />
                                        <TextField
                                            label="Clinic Patient ID"
                                            fullWidth
                                            margin="normal"
                                            value={clinicPatientId}
                                            onChange={(e) => setClinicPatientId(e.target.value)}
                                            disabled={patientType === 'existing'}
                                        />
                                        <TextField
                                            label="Date of Birth"
                                            type="date"
                                            fullWidth
                                            margin="normal"
                                            value={dob}
                                            onChange={(e) => setDob(e.target.value)}
                                            inputProps={{
                                                max: new Date().toISOString().split('T')[0],
                                            }}
                                            InputLabelProps={{ shrink: true }}
                                            disabled={patientType === 'existing'}
                                        />
                                        <TextField
                                            label="Species"
                                            fullWidth
                                            margin="normal"
                                            value={species}
                                            onChange={(e) => setSpecies(e.target.value)}
                                            disabled={patientType === 'existing'}
                                        />
                                        <TextField
                                            label="Breed"
                                            fullWidth
                                            margin="normal"
                                            value={breed}
                                            onChange={(e) => setBreed(e.target.value)}
                                            disabled={patientType === 'existing'}
                                        />
                                        <TextField
                                            label="Microchip Number"
                                            fullWidth
                                            margin="normal"
                                            value={microchip}
                                            onChange={(e) => setMicrochip(e.target.value)}
                                            disabled={patientType === 'existing'}
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}

                <TextField
                    label="Notes"
                    fullWidth
                    multiline
                    rows={3}
                    margin="normal"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="secondary">
                    Cancel
                </Button>
                <Button onClick={handleSave} color="primary" variant="contained" disabled={saving}>
                    {isCombinedMode && hasActiveWhiteboardDrawings ? 'Save both' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SaveCustomisedHandoutModal;
